import { useQuery, useMutation, useQueryClient } from "react-query";
import { api } from "@/lib/api";

// ─── Dashboard ──────────────────────────────────────────────────────────────

export function useLoanDashboard() {
  return useQuery(
    ["loan-dashboard"],
    async () => {
      const { data } = await api.get("/loans/lifecycle/dashboard");
      return data.data;
    },
    { staleTime: 60_000 }
  );
}

// ─── Monthly Repayments ─────────────────────────────────────────────────────

export function useMonthlyRepayments(month: number, year: number, tab: string = "all") {
  return useQuery(
    ["monthly-repayments", month, year, tab],
    async () => {
      const params = new URLSearchParams({ month: String(month), year: String(year), tab });
      const { data } = await api.get(`/loans/lifecycle/monthly-repayments?${params}`);
      return data.data;
    },
    { keepPreviousData: true, staleTime: 30_000 }
  );
}

// ─── Member Loan History ────────────────────────────────────────────────────

export function useMemberLoanHistory(memberId: number | string | null) {
  return useQuery(
    ["member-loan-history", memberId],
    async () => {
      const { data } = await api.get(`/loans/lifecycle/member/${memberId}/history`);
      return data.data;
    },
    { enabled: !!memberId, staleTime: 30_000 }
  );
}

// ─── Repayment Recording ────────────────────────────────────────────────────

export function useRecordRepayment() {
  const queryClient = useQueryClient();
  return useMutation(
    async ({ loanId, payload }: { loanId: number; payload: any }) => {
      const { data } = await api.post(`/loans/lifecycle/${loanId}/repayments`, payload);
      return data;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["monthly-repayments"]);
        queryClient.invalidateQueries(["loan-dashboard"]);
        queryClient.invalidateQueries(["loans"]);
      },
    }
  );
}

// ─── Schedule Generation ────────────────────────────────────────────────────

export function useGenerateSchedule() {
  const queryClient = useQueryClient();
  return useMutation(
    async (loanId: number) => {
      const { data } = await api.post(`/loans/lifecycle/${loanId}/schedule`);
      return data;
    },
    { onSuccess: () => queryClient.invalidateQueries(["loans"]) }
  );
}

// ─── Send Notification ──────────────────────────────────────────────────────

export function useSendLoanNotification() {
  return useMutation(
    async ({ loanId, payload }: { loanId: number; payload: any }) => {
      const { data } = await api.post(`/loans/lifecycle/${loanId}/notify`, payload);
      return data;
    }
  );
}

// ─── Bulk Approve ───────────────────────────────────────────────────────────

export function useBulkApprove() {
  const queryClient = useQueryClient();
  return useMutation(
    async (payload: { loanIds: number[]; action: string; role: string; notes?: string }) => {
      const { data } = await api.post("/loans/lifecycle/bulk-approve", payload);
      return data;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["loans"]);
        queryClient.invalidateQueries(["loan-dashboard"]);
      },
    }
  );
}

// ─── Reports ────────────────────────────────────────────────────────────────

export function usePortfolioReport() {
  return useQuery(
    ["portfolio-report"],
    async () => {
      const { data } = await api.get("/loans/lifecycle/reports/portfolio");
      return data.data;
    },
    { staleTime: 120_000 }
  );
}

export function useRepaymentReport(month: number, year: number) {
  return useQuery(
    ["repayment-report", month, year],
    async () => {
      const params = new URLSearchParams({ month: String(month), year: String(year) });
      const { data } = await api.get(`/loans/lifecycle/reports/repayments?${params}`);
      return data.data;
    },
    { staleTime: 60_000 }
  );
}

export function useOverdueReport() {
  return useQuery(
    ["overdue-report"],
    async () => {
      const { data } = await api.get("/loans/lifecycle/reports/overdue");
      return data.data;
    },
    { staleTime: 60_000 }
  );
}

export function useDisbursementReport(month: number, year: number) {
  return useQuery(
    ["disbursement-report", month, year],
    async () => {
      const { data } = await api.get(`/loans/lifecycle/reports/disbursement?month=${month}&year=${year}`);
      return data.data;
    },
    { staleTime: 60_000 }
  );
}

export function useInterestIncomeReport() {
  return useQuery(
    ["interest-income-report"],
    async () => {
      const { data } = await api.get("/loans/lifecycle/reports/interest-income");
      return data.data;
    },
    { staleTime: 60_000 }
  );
}

export function useApprovalAuditReport() {
  return useQuery(
    ["approval-audit-report"],
    async () => {
      const { data } = await api.get("/loans/lifecycle/reports/approval-audit");
      return data.data;
    },
    { staleTime: 60_000 }
  );
}

export function useCollectionPerformanceReport() {
  return useQuery(
    ["collection-performance-report"],
    async () => {
      const { data } = await api.get("/loans/lifecycle/reports/collection-performance");
      return data.data;
    },
    { staleTime: 60_000 }
  );
}

export function useMemberStatement(identifier: string) {
  return useQuery(
    ["member-statement", identifier],
    async () => {
      const { data } = await api.get(`/loans/lifecycle/reports/member-statement/${identifier}`);
      return data.data;
    },
    { enabled: !!identifier, staleTime: 60_000 }
  );
}

// ─── Loans List (paginated) ─────────────────────────────────────────────────

export function useLoansList(params: { page: number; limit: number; search?: string; status?: string }) {
  return useQuery(
    ["loans", params.page, params.limit, params.search, params.status],
    async () => {
      const searchParams = new URLSearchParams({
        page: String(params.page),
        limit: String(params.limit),
      });
      if (params.search) searchParams.set("search", params.search);
      if (params.status) searchParams.set("status", params.status);
      const { data } = await api.get(`/loans?${searchParams}`);
      return data;
    },
    { keepPreviousData: true }
  );
}

// ─── LOAN REPAYMENTS (NEW) ─────────────────────────────────────────────────

// Member submits repayment
export function useSubmitLoanRepayment() {
  const queryClient = useQueryClient();
  return useMutation(
    async ({ loanId, payload }: { loanId: number; payload: any }) => {
      const { data } = await api.post(`/loan-repayments/loan/${loanId}/repayments`, payload);
      return data;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["loan-repayments"]);
        queryClient.invalidateQueries(["loan-dashboard"]);
        queryClient.invalidateQueries(["loan-detail"]);
      },
    }
  );
}

// Admin records repayment
export function useRecordLoanRepayment() {
  const queryClient = useQueryClient();
  return useMutation(
    async ({ loanId, payload }: { loanId: number; payload: any }) => {
      const { data } = await api.post(`/loan-repayments/loan/${loanId}/record`, payload);
      return data;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["loan-repayments"]);
        queryClient.invalidateQueries(["loan-dashboard"]);
        queryClient.invalidateQueries(["loan-detail"]);
        queryClient.invalidateQueries(["loans"]);
      },
    }
  );
}

// Approve repayment
export function useApproveRepayment() {
  const queryClient = useQueryClient();
  return useMutation(
    async (id: number) => {
      const { data } = await api.patch(`/loan-repayments/${id}/approve`);
      return data;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["loan-repayments"]);
        queryClient.invalidateQueries(["loan-dashboard"]);
        queryClient.invalidateQueries(["loan-payroll-deductions"]);
      },
    }
  );
}

// Reject repayment
export function useRejectRepayment() {
  const queryClient = useQueryClient();
  return useMutation(
    async ({ id, reason }: { id: number; reason?: string }) => {
      const { data } = await api.patch(`/loan-repayments/${id}/reject`, { reason });
      return data;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["loan-repayments"]);
        queryClient.invalidateQueries(["loan-dashboard"]);
      },
    }
  );
}

// Send to payroll
export function useSendToPayroll() {
  const queryClient = useQueryClient();
  return useMutation(
    async (id: number) => {
      const { data } = await api.patch(`/loan-repayments/${id}/send-to-payroll`);
      return data;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["loan-repayments"]);
        queryClient.invalidateQueries(["loan-payroll-deductions"]);
      },
    }
  );
}

// Mark deducted
export function useMarkDeducted() {
  const queryClient = useQueryClient();
  return useMutation(
    async (id: number) => {
      const { data } = await api.patch(`/loan-repayments/${id}/mark-deducted`);
      return data;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["loan-repayments"]);
        queryClient.invalidateQueries(["loan-payroll-deductions"]);
        queryClient.invalidateQueries(["loan-dashboard"]);
        queryClient.invalidateQueries(["loan-detail"]);
      },
    }
  );
}

// Get monthly repayments (with verification workflow)
export function useLoanRepayments(month: number, year: number, tab: string = "all") {
  return useQuery(
    ["loan-repayments", month, year, tab],
    async () => {
      const params = new URLSearchParams({ month: String(month), year: String(year), tab });
      const { data } = await api.get(`/loan-repayments/monthly-repayments?${params}`);
      return data.data;
    },
    { keepPreviousData: true, staleTime: 30_000 }
  );
}

// Get loan payroll deductions (for HR)
export function useLoanPayrollDeductions(month: number, year: number, status?: string) {
  return useQuery(
    ["loan-payroll-deductions", month, year, status],
    async () => {
      const params = new URLSearchParams({ month: String(month), year: String(year) });
      if (status) params.set("status", status);
      const { data } = await api.get(`/loan-repayments/loan-deductions?${params}`);
      return data.data;
    },
    { keepPreviousData: true, staleTime: 30_000 }
  );
}

// ─── Loan Reminders ─────────────────────────────────────────────────────────

export function useMemberReminders() {
  return useQuery(
    ["member-reminders"],
    async () => {
      const { data } = await api.get("/member/loans/reminders");
      return data.data;
    },
    { staleTime: 30_000 }
  );
}

export function useLoanReminders(loanId: number | null) {
  return useQuery(
    ["loan-reminders", loanId],
    async () => {
      const { data } = await api.get(`/loans/${loanId}/reminders`);
      return data.data;
    },
    { enabled: !!loanId, staleTime: 30_000 }
  );
}

export function useOverdueSummary() {
  return useQuery(
    ["overdue-summary"],
    async () => {
      const { data } = await api.get("/loans/reminders/overdue-summary");
      return data.data;
    },
    { staleTime: 30_000 }
  );
}

export function useReminderHistory(filters?: { status?: string; reminderType?: string; page?: number; limit?: number }) {
  return useQuery(
    ["reminder-history", filters],
    async () => {
      const params = new URLSearchParams();
      if (filters?.status) params.set("status", filters.status);
      if (filters?.reminderType) params.set("reminderType", filters.reminderType);
      if (filters?.page) params.set("page", String(filters.page));
      if (filters?.limit) params.set("limit", String(filters.limit));
      const { data } = await api.get(`/loans/reminders/history?${params}`);
      return data;
    },
    { keepPreviousData: true, staleTime: 30_000 }
  );
}

export function useSendReminder() {
  const queryClient = useQueryClient();
  return useMutation(
    async ({ loanId, reminderId }: { loanId: number; reminderId: number }) => {
      const { data } = await api.post(`/loans/${loanId}/send-reminder`, { reminderId });
      return data;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries("member-reminders");
        queryClient.invalidateQueries("reminder-history");
        queryClient.invalidateQueries("overdue-summary");
      },
    }
  );
}

export function useBulkSendReminders() {
  const queryClient = useQueryClient();
  return useMutation(
    async (reminderIds: number[]) => {
      const { data } = await api.post("/loans/reminders/bulk-send", { reminderIds });
      return data;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries("member-reminders");
        queryClient.invalidateQueries("reminder-history");
        queryClient.invalidateQueries("overdue-summary");
      },
    }
  );
}

export function useRunReminderScan() {
  const queryClient = useQueryClient();
  return useMutation(
    async () => {
      const { data } = await api.post("/loans/reminders/run");
      return data;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries("member-reminders");
        queryClient.invalidateQueries("reminder-history");
        queryClient.invalidateQueries("overdue-summary");
      },
    }
  );
}
